import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Star } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, Pill, fmtNum, IataBadge } from "../components/common";
import { airlineLogo } from "../lib/assets";
import { Pagination } from "./Airports";

export default function AirlinesDirectory() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [active, setActive] = useState("Y");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/airline-countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/airlines", { params: { q, active, country, page, limit: 24 } });
      setData(data);
    } finally { setLoading(false); }
  }, [q, active, country, page]);

  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setPage(1); }, [q, active, country]);
  useEffect(() => { setParams(q ? { q } : {}, { replace: true }); }, [q, setParams]);

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div data-testid="airlines-directory-page">
      <PageHeader eyebrow="Directory" title="All Airlines A–Z"
        subtitle="The complete alphabetical directory of 6,000+ carriers worldwide. Open any airline for its profile, discussion board and complaint form." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Link to="/airlines" data-testid="back-to-popular-link"
          className="inline-flex items-center gap-1.5 text-sm font-700 text-primary hover:underline mb-4">
          <Star className="w-4 h-4" /> Back to Popular Airlines
        </Link>
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input data-testid="airlines-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={Boolean(params.get("q"))}
            placeholder="Search by name or IATA/ICAO code…"
            className="flex-1 border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-colors" />
          <select data-testid="airlines-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
            className="border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary max-w-[220px]">
            <option value="">All countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="airlines-active-filter" value={active} onChange={(e) => setActive(e.target.value)}
            className="border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary">
            <option value="Y">Active only</option>
            <option value="N">Inactive only</option>
            <option value="">All</option>
          </select>
        </div>

        {loading ? <Spinner /> : (
          <>
            <div className="text-sm text-muted-foreground mb-3 font-mono">{fmtNum(data?.total)} results · alphabetical</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data?.items.map((a) => (
                <Link key={a.id} to={`/airlines/${a.id}`} data-testid={`airline-card-${a.id}`}
                  className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                  <IataBadge code={a.iata || a.icao || "—"} name={a.name} logo={airlineLogo(a.iata)} className="h-28" />
                  <div className="p-5">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />{a.country || "—"}
                    </div>
                    <div className="mt-3">{a.active === "Y" ? <Pill tone="green">Active</Pill> : <Pill>Inactive</Pill>}</div>
                  </div>
                </Link>
              ))}
            </div>
            {data?.items.length === 0 && <div className="py-16 text-center text-muted-foreground">No airlines found.</div>}
            {totalPages > 1 && <Pagination page={page} totalPages={totalPages} setPage={setPage} />}
          </>
        )}
      </div>
    </div>
  );
}
